/**
 * Every custom background is re-encoded to exactly these dimensions.
 *
 * **They are module constants on purpose, and that is what makes the feature safe.** Agora's processor
 * fails to build a WebGL texture from an image with an odd width or height (`texture bound to texture
 * unit 2 is not renderable…`) and silently applies no background — see
 * `public/assets/backgrounds/README.md`. Because the destination canvas size is fixed and even here, no
 * input image can produce odd output, so we never have to validate the user's dimensions. Make this
 * size dynamic and the trap comes straight back.
 *
 * 1280x720 also matches the 16:9 the bundled artwork is authored at and the `480p_9` encoder preset the
 * pilot shipped.
 */
export const BACKGROUND_WIDTH = 1280;
export const BACKGROUND_HEIGHT = 720;

/** 2x the 72px picker tile so it stays sharp on retina. Still 16:9, still even. */
export const THUMBNAIL_WIDTH = 192;
export const THUMBNAIL_HEIGHT = 108;

/**
 * Tuned against the localStorage budget rather than for visual fidelity: at q0.82 a 1280x720 photo
 * lands around 120 KB, or ~165K characters once base64-encoded, which is what lets 8 backgrounds fit
 * in `CUSTOM_BACKGROUNDS_CHAR_BUDGET`. The image sits behind a person and is never the subject, so the
 * quality cost is not noticeable.
 */
export const WEBP_QUALITY = 0.82;

/**
 * A decode-safety valve, **not** a dimension check — the product requirement is that any image size
 * and aspect ratio is accepted. This only stops a pathologically large file (e.g. a 100 MP scan)
 * exhausting memory in `createImageBitmap`; output size is fixed regardless of input.
 */
export const MAX_SOURCE_BYTES = 15 * 1024 * 1024;

/**
 * What the user may pick. WebP is only the *storage* format — everything here is re-encoded, so the
 * input format never survives.
 *
 * Excluded on purpose: GIF (only the first frame would render, which reads as broken rather than
 * static), SVG (`createImageBitmap` support is unreliable and they can carry script), HEIC (iPhone's
 * default, but Chrome can't decode it and Safari is already gated off by `BackgroundControl`).
 */
export const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const ACCEPTED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'] as const;

/** The `accept` attribute for the file input, kept next to the list it has to agree with. */
export const FILE_INPUT_ACCEPT = ACCEPTED_MIME_TYPES.join(',');
